import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Share2, Trash2 } from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { SearchableSelector } from "@/components/searchable-selector";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/auth/auth-provider";
import { hasCapability } from "@/auth/capabilities";

export function ControllerSharingPanel({ deviceId }: { deviceId: string }): JSX.Element {
  const { me } = useAuth();
  if (!hasCapability(me, "controllers:share")) return <></>;
  return <SharingPanel deviceId={deviceId} />;
}

function SharingPanel({ deviceId }: { deviceId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [customerTenantId, setCustomerTenantId] = useState("");
  const companiesQuery = useQuery({
    queryKey: ["distribution-companies"],
    queryFn: () => api.distributionCompanies(),
  });
  const sharesQuery = useQuery({
    queryKey: ["controller-shares", deviceId],
    queryFn: () => api.controllerShares(deviceId),
  });
  const distributionCompanies = companiesQuery.data ?? [];

  const refreshShares = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["controller-shares", deviceId] });
  };
  const shareMutation = useMutation({
    mutationFn: () => api.shareController(deviceId, Number(customerTenantId)),
    onSuccess: () => {
      setCustomerTenantId("");
      refreshShares();
      toast({ title: "Controller shared", variant: "success" });
    },
    onError: (error: unknown) => toast({
      title: "Could not share controller",
      description: error instanceof Error ? error.message : "Unknown error",
      variant: "destructive",
    }),
  });
  const revokeMutation = useMutation({
    mutationFn: (tenantId: number) => api.revokeControllerShare(deviceId, tenantId),
    onSuccess: refreshShares,
    onError: (error: unknown) => toast({
      title: "Could not revoke access",
      description: error instanceof Error ? error.message : "Unknown error",
      variant: "destructive",
    }),
  });
  const alreadyShared = new Set((sharesQuery.data ?? []).map((share) => share.customer_tenant_id));
  const availableCompanies = distributionCompanies.filter(
    (company) => !alreadyShared.has(company.tenant_id),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Share2 className="h-4 w-4" /> Distribution company access</CardTitle>
        <p className="text-sm text-muted-foreground">
          Shared companies can view all retained controller history. Revoking access also removes access to that history.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {companiesQuery.error || sharesQuery.error ? (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            Could not load sharing settings.
          </div>
        ) : null}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1 space-y-2">
            <Label>Distribution company</Label>
            <SearchableSelector label="Distribution company" size="form" value={customerTenantId}
              onChange={setCustomerTenantId} placeholder="Select a company"
              options={availableCompanies.map((company) => ({ value: String(company.tenant_id), label: company.name }))} />
          </div>
          <Button
            disabled={!customerTenantId || shareMutation.isPending || availableCompanies.length === 0}
            onClick={() => shareMutation.mutate()}
          >
            {shareMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
            Share controller
          </Button>
        </div>
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Current access</h3>
          {sharesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading access…</p>
          ) : (sharesQuery.data ?? []).length === 0 ? (
            <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
              This controller is not shared with a distribution company.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {sharesQuery.data?.map((share) => (
                <li key={share.customer_tenant_id} className="flex items-center justify-between gap-3 px-3 py-3">
                  <span className="min-w-0 truncate text-sm font-medium">{share.customer_name}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={revokeMutation.isPending}
                    aria-label={`Revoke access from ${share.customer_name}`}
                    onClick={() => revokeMutation.mutate(share.customer_tenant_id)}
                  >
                    <Trash2 className="h-4 w-4" /> Revoke
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
